import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "utils/apiClient";
import { bearerGuestToken, ensureGuestSession } from "utils/guestSession";
import { oCareSubscriptionFeatureLabels } from "../../workspaces/subscriptionFeatures";
import { Footer } from "../../components/care/CareLayout";
import { PublicNav } from "./LandingPage";

const pageContent = (value) => Array.isArray(value) ? value : value?.content || [];
const firstText = (...values) => values.map((value) => String(value ?? "").trim()).find(Boolean) || "";
const planSignal = (plan = {}) => firstText(plan.code, plan.name, plan.subscriptionPlanCode, plan.subscriptionPlanName, plan.tier).toUpperCase();
const planTier = (plan = {}) => {
  const signal = planSignal(plan);
  if (signal.includes("FREE")) return "FREE";
  if (signal.includes("STANDARD")) return "STANDARD";
  if (signal.includes("PREMIUM")) return "PREMIUM";
  return "OTHER";
};
const planRank = (plan) => ({ FREE: 0, STANDARD: 1, PREMIUM: 2, OTHER: 3 }[planTier(plan)]);
const planName = (plan) => {
  const name = firstText(plan.name, plan.subscriptionPlanName, plan.code, "OCarely");
  return /\bplan$/i.test(name) ? name : `${name} Plan`;
};
const planDescription = (plan) => {
  const description = firstText(plan?.description);
  if (planTier(plan) === "FREE") return "Free subscription plan with basic access.";
  if (planTier(plan) === "STANDARD" && /^free(?: business)? subscription plan\b/i.test(description)) {
    return "Complete AFH care operations, compliance, documentation, staffing, eMAR, eFax, and website tools.";
  }
  return description || "AFH management tools for clearer care operations and compliance.";
};
const money = (value, currency = "USD") => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "Contact us";
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(amount);
};
const normalizedFeatures = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => typeof item === "string" ? item : firstText(item?.label, item?.name, item?.title, item?.description)).filter(Boolean);
  }
  if (typeof value === "string") return value.split(/\r?\n|[;•]/).map((item) => item.trim()).filter(Boolean);
  return [];
};
const planFeatures = (plan) => {
  const tier = planTier(plan);
  if (tier === "STANDARD" || tier === "PREMIUM") return oCareSubscriptionFeatureLabels(tier);
  const supplied = normalizedFeatures(
    plan.features || plan.benefits || plan.includedServices || plan.services || plan.serviceOfferings
      || plan.metadata?.features || plan.metadata?.benefits || plan.metadata?.includedServices,
  );
  return supplied.length ? supplied : oCareSubscriptionFeatureLabels(tier);
};

export default function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    let active = true;
    const loadPlans = async () => {
      setLoading(true);
      setError("");
      try {
        const guestSession = await ensureGuestSession();
        const authorization = bearerGuestToken(guestSession);
        if (!authorization) throw new Error("A guest session could not be created.");
        const requestConfig = (code) => ({
          params: { code, status: "ACTIVE", type: "BUSINESS", page: 0, size: 100 },
          headers: { Authorization: authorization, "O-SKIP-WORKSPACE-CONTEXT": "1" },
          skipAuthContext: true,
        });
        const [careResponse, freeResponse] = await Promise.all([
          apiClient.get("/payment-service/api/v1/subscription-plans", requestConfig("CARE")),
          apiClient.get("/payment-service/api/v1/subscription-plans", requestConfig("FREE_BUSINESS")),
        ]);
        if (active) {
          const uniquePlans = new Map();
          [...pageContent(careResponse?.data), ...pageContent(freeResponse?.data)].forEach((plan) => {
            const tier = planTier(plan);
            const key = tier === "OTHER" ? plan.id || plan.code || planName(plan) : tier;
            const current = uniquePlans.get(key);
            const canonicalFree = tier === "FREE" && String(plan.code || "").toUpperCase() === "FREE_BUSINESS";
            if (!current || canonicalFree) uniquePlans.set(key, plan);
          });
          setPlans([...uniquePlans.values()].sort((left, right) => planRank(left) - planRank(right)));
        }
      } catch (requestError) {
        if (active) setError(requestError?.response?.data?.message || requestError?.message || "Pricing is temporarily unavailable.");
      } finally {
        if (active) setLoading(false);
      }
    };
    loadPlans();
    return () => {
      active = false;
    };
  }, []);

  const visiblePlans = useMemo(() => plans.filter((plan) => planTier(plan) !== "OTHER" || planName(plan)), [plans]);

  return (
    <div className="page public-page pricing-page">
      <PublicNav />
      <main>
        <section className="pricing-heading">
          <h1>Choose the Subscription Plan that Meets Your Need...</h1>
          <p>Stay compliant, reduce avoidable costs, and give your team one clear place to run daily care operations.</p>
        </section>

        <section className="pricing-plans" aria-live="polite">
          {loading ? <div className="pricing-status"><i className="ti ti-loader-2" /> Loading current plans...</div> : null}
          {!loading && error ? (
            <div className="pricing-status is-error">
              <strong>Unable to load pricing</strong>
              <span>{error}</span>
            </div>
          ) : null}
          {!loading && !error && !visiblePlans.length ? <div className="pricing-status">No active plans are currently available.</div> : null}
          {!loading && !error ? visiblePlans.map((plan) => {
            const tier = planTier(plan);
            const features = planFeatures(plan);
            return (
              <article className={`pricing-plan is-${tier.toLowerCase()}`} key={plan.id || plan.code || planName(plan)}>
                <div className="pricing-plan-heading">
                  <span>{tier === "OTHER" ? "OCarely" : tier}</span>
                  <h2>{planName(plan)}</h2>
                  <p>{planDescription(plan)}</p>
                </div>
                {tier === "FREE" ? (
                  <div className="pricing-plan-included">
                    <strong>Included</strong>
                    <span>Included when no paid subscription is active.</span>
                  </div>
                ) : null}
                <div className="pricing-plan-rates">
                  <span><strong>{money(plan.monthlyAmount, plan.currency)}</strong><small>per month</small></span>
                  <span><strong>{money(plan.annualAmount, plan.currency)}</strong><small>per year</small></span>
                </div>
                {features.length ? (
                  <ul>
                    {features.map((feature) => <li key={feature}><i className="ti ti-check" />{feature}</li>)}
                  </ul>
                ) : null}
                <Link className="btn pricing-plan-cta" to="/get-started">
                  Get Started
                </Link>
              </article>
            );
          }) : null}
        </section>
      </main>
      <Footer publicLinks />
    </div>
  );
}
